/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import config from "../config/featuresSection.config.json";
import formConfig from "../config/kycDocForm.config.json";
import profileFormConfig from "../config/profileForm.config.json";

import jwt_decode from "jwt-decode";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_MY_PROFILE } from "../graphql";
import { VApplication } from "../../../application";
import moment from "moment";
import { toHeaderCase, toLowerCase, toSentenceCase } from "js-convert-case";
import {StorageModule} from "@vlinder-web/storage-module-react";
import { AppNavigatorService } from "../../appNavigatorService";

const getWhoamiSrv = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.AppNavigatorService") as AppNavigatorService;
};

export interface IFeatureSectionMethods extends IPluginMethodMap {
  getFeatureSectionProps(args: any): Promise<any>;
}

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

const lookup = require("country-code-lookup");

export class FeatureSectionService implements IAgentPlugin {
  readonly methods: IFeatureSectionMethods;

  constructor() {
    this.methods = {
      getFeatureSectionProps: this.getProps.bind(this),
    };
  }

  async getProps(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    const storageSrv = StorageModule.getInstance();
    let vuid = jwt_decode(getWhoamiSrv()?.getToken());
    try {
      if (client) {
        const res = await client.query({
          query: GET_MY_PROFILE,
          variables: {
            input1: {
              limit: 100,
              where: [
                {
                  column: "txnOwner",
                  op: "Equal",
                  value: [vuid?.vuid],
                },
                {
                  column: "type",
                  op: "In",
                  value: ["KYC_SUBMISSION"],
                },
              ],
            },
          },
          fetchPolicy: "network-only",
        });
        if (res?.data?.getMyProfile) {
          console.log("service data", res?.data?.getMyProfile);
          storageSrv.set("myProfile", res?.data?.getMyProfile, "session");
          let _res = res?.data?.getMyProfile;
          let _txtResult = res?.data?.getTxnList?.data;
          return {
            title: `${toHeaderCase(_res?.firstName ?? "")} ${toHeaderCase(
              _res?.lastName ?? ""
            )}`,
            subtitle: _res?.vuid ? `Account ID: ${_res?.vuid}` : _res?.occupation,
            timelineData: res?.data?.getTxnList,
            phoneNumber: _res?.primaryContactNo,
            phoneVerified: _res?.phoneVerified,
            kycSubmitted:_res?.kycSubmitted,
            email: _res?.email,
            heroImage:{
              image:
              _res?.image ??
              `https://ui-avatars.com/api/?name=${_res.firstName}+${_res?.lastName}&background=0D8ABC&color=fff`,
            },
            listItems: this.getListItems(_res),
            carouselItems: this.getCarouselItems(_res?.kyc),
            buttons: await this.checkVerifyPhoneButton(
              this.getButtons(_res),
              _res
            ),
            profileStatus: this.getStatus(_res?.kyc),
            profileStatusDescription: this.getStatusDesc(_res?.kyc),
            initialData: {
              firstName: _res?.firstName,
              lastName: _res?.lastName,
              image: _res?.image,
              primaryContactNo: _res?.primaryContactNo,
              occupation: _res?.occupation,
              streetAddress: _res?.address?.addressLine1,
              city: _res?.address?.city,
              country: _res?.address?.country,
            },
            profileForms: {
              fields: this.getProfileFields(_res),
            },
            kycForms: {
              fields: this.getKycFields(_res),
              layout: formConfig?.layout,
              validatorConfig: formConfig?.validatorConfig,
              initialData: this.getInitialDocs(_res),
            },
            accordionItems:this.getAccordionItems(_txtResult),
          };
        }
      }
    } catch (err: any) {
      console.log(`Sign In user error`, err?.response?.data);
      throw Error(err?.response?.data?.error?.display_message);
    }
    // return { ...(config || {}) };
  }

  private getAccordionItems(txList:any){
    let items:any = [];
    if(txList?.length > 0){
      let timelineDetails = this.getTimeLine(txList);
      items.push({
        type:"timeline",
        title:config?.timelineDetails?.title,
        data:[...timelineDetails],
        lineColor:"#262B40",
        horizontalStepperColors:{
          DONE:"#56DCBA",
          UNDONE:"#56DCBA",
          LAST:"#56DCBA",
          CURRENT:"#56DCBA"
        }
      })
    }
    return items;
  }

  private getTimeLine(data: any) {
    let _temp: any = [];
    Array.isArray(data) && data?.forEach(element => {
        _temp.push(
            {
                "key": element?.id,
                "headline": toHeaderCase(element?.action ?? ""),
                "headlineBackgroundColor": "#579783",
                "children": this.getChildren(element)
            }
        )
    });

    return _temp;
}

private getChildren(data: any) {
  let _temp: any = [];
  _temp.push({
      "itemId": data?.id,
      multiColList: this.getProvenanceAccordion(data),
      "title": toHeaderCase(data?.action ?? ""),
      "subtitle": data?.payload?.comment,
  });

  return _temp;
}

private getProvenanceAccordion(res: any) {
  let _temp: any = [];
  if (res) {
    let _list: any = [];
    let _date: any = [];
    let _location: any = [];
    if (res?.performedBy?.firstName) {
      _list.push({
        title: toHeaderCase("by"),
        subTitle: `${toHeaderCase(res?.performedBy?.firstName)} ${toHeaderCase(res?.performedBy?.lastName)}`,
        icon: config?.orgIcon
      })
    }
    if(res?.location){
      _location.push({
        title: toHeaderCase("at"),
          subTitle: res?.location ?? "-",
          icon: config?.locationIcon
      })
    }
    if (res?.when) {
        _date.push({
          title: toHeaderCase("on"),
          subTitle: res?.when ? moment(parseInt(res?.when)).format("LLL"): "-",
          icon: config?.dateIcon
        })
    }
    _temp = [..._list, ..._location, ..._date]
  }
  return {
    "numberOfColumns": "2",
    data: _temp
  };
}

  private async checkVerifyPhoneButton(buttons: any, res: any) {
    console.log("buttons", buttons);
    let _buttons = buttons;
    if (! res?.phoneVerified && res?.kycSubmitted) {
      _buttons.push(config?.verifyPhoneButton);
    }
    return _buttons;
  }

  private getListItems(res: any) {
    let _temp = [];
    _temp = config?.listItems?.map((item) => {
      if (item?.title === "Full Name") {
        return {
          ...item,
          subTitle: `${toHeaderCase(res?.firstName ?? "")} ${toHeaderCase(
            res?.lastName ?? ""
          )}`,
        };
      }
      if (item?.title === "Street Address") {
        return {
          ...item,
          subTitle: res?.address?.addressLine1
            ? `${res?.address?.addressLine1}`
            : "-",
        };
      }
      if (item?.title === "City") {
        return {
          ...item,
          subTitle: res?.address?.city ?? "-",
        };
      }
      if (item?.title === "Country") {
        return {
          ...item,
          subTitle: res?.address?.country
            ? lookup.byIso(`${res?.address?.country}`)?.country
            : "-",
          flag: res?.address?.country,
        };
      }
      if (item?.title === "Email") {
        return {
          ...item,
          subTitle: res?.email ?? "-",
          verified: res?.emailVerified,
        };
      }
      if (item?.title === "Telephone") {
        return {
          ...item,
          subTitle: res?.primaryContactNo ?? "-",
          verified: res?.phoneVerified,
        };
      }
      if (item?.title === "Occupation") {
        return {
          ...item,
          subTitle: res?.occupation ?? "-",
        };
      }
    });
    return _temp;
  }

  private getStatus(items: any) {
    let tempStatus = items && items[0];
    if (tempStatus?.status) {
      return toLowerCase(tempStatus?.status ?? "");
    } else return null;
  }

  private getStatusDesc(items: any) {
    let tempStatus = items && items[0];
    if (tempStatus?.comment) {
      return toSentenceCase(tempStatus?.comment ?? "");
    } else return null;
  }

  private getCarouselItems(items: any) {
    let _temp = [];
    _temp = Array.isArray(items) &&
      items?.map((ele: any) => {
        return {
          ...ele,
          type: this.getDocType(ele?.fileType),
          src: ele?.doc,
          title: ele?.name,
          poster: ele?.poster,
        };
      });
    return _temp;
  }

  private getDocType(res:any) {
    switch(res){
      case "application/pdf":
        return "pdf";
      case "image/png":
        return "img";
      case "image/jpg":
        return "img";
      case "image/jpeg":
        return "img";
      default: return null;
    }
  }

  private getButtons(res: any) {
    let _buttons = config?.buttons;
    let _temp1 = res.kyc?.find((i) => i?.type === "ADDRESS_PROOF");
    let _temp2 = res.kyc?.find((i) => i?.type === "ID_PROOF");
    console.log(`kyc docs`, res?.phoneVerified);

    if (
      (!_temp1 ||
        _temp1?.status === "SUBMITTED" ||
        _temp1?.status === "REJECTED") &&
      (!_temp2 ||
        _temp2?.status === "SUBMITTED" ||
        _temp2?.status === "REJECTED") &&
        res?.kycSubmitted
    ) {
      _buttons.push(config?.kycButton);
    } else if (
      (!_temp1 ||
      _temp1?.status === "SUBMITTED" ||
      _temp1?.status === "REJECTED") &&
      res?.kycSubmitted
    ) {
      _buttons.push(config?.updateAddressProof);
    } else if (
      (!_temp2 ||
      _temp2?.status === "SUBMITTED" ||
      _temp2?.status === "REJECTED") &&
      res?.kycSubmitted
    ) {
      _buttons.push(config?.updateIdProof);
    }
    return [...new Map(_buttons.map((item) => [item["key"], item])).values()];
  }

  private getProfileFields(res: any) {
    let _fields = profileFormConfig?.FIELDS;
    if (res?.phoneVerified) {
      let formFields = _fields?.filter(
        (i) => i.compProps?.id !== "primaryContactNo"
      );
      return formFields;
    } else {
      return _fields;
    }
  }

  private getKycFields(res: any) {
    let _temp1 = res.kyc?.find((i) => i?.type === "ADDRESS_PROOF");
    let _temp2 = res.kyc?.find((i) => i?.type === "ID_PROOF");
    if (
      (!_temp1 ||
        _temp1?.status === "SUBMITTED" ||
        _temp1?.status === "REJECTED") &&
      (!_temp2 ||
        _temp2?.status === "SUBMITTED" ||
        _temp2?.status === "REJECTED")
    ) {
      return formConfig?.FIELDS;
    } else if (
      !_temp1 ||
      _temp1?.status === "SUBMITTED" ||
      _temp1?.status === "REJECTED"
    ) {
      let filterIds = ["addressProofType", "addressProof"];
      let filteredArray = formConfig?.FIELDS?.filter((i) =>
        filterIds.includes(i.compProps?.id)
      );
      return filteredArray;
    } else if (
      !_temp2 ||
      _temp2?.status === "SUBMITTED" ||
      _temp2?.status === "REJECTED"
    ) {
      let filterIds = ["idProofType", "idProof"];
      let filteredArray = formConfig?.FIELDS?.filter((i) =>
        filterIds.includes(i.compProps?.id)
      );
      return filteredArray;
    }
  }

  private getInitialDocs(res: any) {
    let _addressProof = {};
    let _idProof = {};
    let _temp1 = res.kyc?.find((i) => i?.type === "ADDRESS_PROOF");
    let _temp2 = res.kyc?.find((i) => i?.type === "ID_PROOF");
    if (_temp1?.type) {
      _addressProof = {
        addressProofType: _temp1?.name,
      };
    }
    if (_temp2?.type) {
      _idProof = {
        idProofType: _temp2?.name,
      };
    }

    return {
      ..._addressProof,
      ..._idProof,
    };
  }

  private getTransactionDetail(items: any) {
    let _temp = [];
    _temp =
      Array.isArray(items?.data) &&
      items?.data?.map((ele) => {
        console.log(`date when:`, new Date(ele?.when));
        return {
          title: toHeaderCase(ele?.action),
          cardTitle: toHeaderCase(ele?.action),
          cardSubtitle: ele?.payload?.policyNumber,
          cardDetailedText: ele?.when
            ? moment(parseInt(ele?.when)).format("LLL")
            : null,
        };
      });

    return {
      title: config?.timelineDetails?.title,
      listItems: _temp,
    };
  }
}
